import React from 'lib/react';
import { useSelector } from 'lib/redux';
import { EVGrid, EVTypography, makeStyles } from 'lib/components';
import { useTranslation } from 'lib/translation';
import VirtualizedTable from 'commonComponents/virtualizedTable/VirtualizedTable.component';
import useMediaQuery from 'hooks/useMediaQuery';
import { getReportDetailsColumns } from '../OrderSubmitted.utils';

const useStyles = makeStyles((theme) => ({
  reportDetails: {
    border: `1px solid ${theme.evPalette.charcoal[300]}`,
    borderRadius: '4px',
    '& tbody>tr:last-child>td': { border: 'none' },
  },
  tableHeader: {
    '& th': {
      backgroundColor: theme.evPalette.charcoal[100],
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      border: 'none',
    },
  },
  tableCell: {
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    color: theme.evPalette.charcoal[900],
  },
  reportDetail: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    padding: '16px 0 32px 0',
    borderBottom: `1px solid ${theme.evPalette.charcoal[300]}`,
    color: theme.evPalette.charcoal[900],
  },
  reportDetailProductType: {
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '18px',
  },
  reportDetailAddress: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '24px',
  },
  reportDetailRow: {
    columnGap: '48px',
    '&>div>p': {
      '&:first-child': {
        textTransform: 'uppercase',
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '18px',
        color: theme.evPalette.charcoal[700],
      },
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '24px',
    },
  },
}));

function ReportDetails() {
  const { reportDetails, bluePrintId } = useSelector(({ orderSubmitted }) => orderSubmitted);
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);

  if (isMobile) {
    return reportDetails.map(({
      product, address, reportId, delivery, building,
    }) => (
      <EVGrid id="order-submitted-report-details-table" key={reportId} className={classes.reportDetail}>
        <EVTypography className={classes.reportDetailProductType}>{product}</EVTypography>
        {(bluePrintId === '') && <EVTypography className={classes.reportDetailAddress}>{address}</EVTypography>}
        <EVGrid container className={classes.reportDetailRow}>
          <EVGrid>
            <EVTypography>{t('order_submitted__report_number')}</EVTypography>
            <EVTypography>{reportId}</EVTypography>
          </EVGrid>
          <EVGrid>
            <EVTypography>{t('common_delivery')}</EVTypography>
            <EVTypography>{delivery}</EVTypography>
          </EVGrid>
          {(bluePrintId === '') ? (
            <EVGrid>
              <EVTypography>{t('order_submitted__building_id')}</EVTypography>
              <EVTypography>{building}</EVTypography>
            </EVGrid>
          ) : (
            <EVGrid>
              <EVTypography>{t('blueprint_order__blueprint_id')}</EVTypography>
              <EVTypography>{bluePrintId}</EVTypography>
            </EVGrid>
          )}
        </EVGrid>
      </EVGrid>
    ));
  }

  return (
    <VirtualizedTable
      id="order-submitted-report-details-table"
      columns={getReportDetailsColumns(t, bluePrintId).map((column) => ({ ...column, cellProps: { className: classes.tableCell } }))}
      dataList={reportDetails}
      rowKey="reportId"
      headerProps={{ className: classes.tableHeader }}
      className={classes.reportDetails}
    />
  );
}

export default ReportDetails;
